import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    MapPin,
    Phone,
    Clock,
    MessageCircle,
    Send,
    Navigation,
} from "lucide-react";
import { FadeUp, MaskedLine } from "@/components/Reveal";
import { CONTACTS, SERVICES, whatsappLink } from "@/data/site";

const inputCls =
    "w-full rounded-xl border border-line bg-white px-4 py-3.5 text-sm font-light text-ink placeholder:text-muted/70 outline-none transition-all duration-300 focus:border-petrol focus:ring-2 focus:ring-petrol/15";

export default function Contatti() {
    const [params] = useSearchParams();
    const [form, setForm] = useState({
        nome: "",
        telefono: "",
        servizio: params.get("servizio") || "",
        messaggio: "",
    });
    const [errors, setErrors] = useState({});

    const set = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = (e) => {
        e.preventDefault();
        const errs = {};
        if (!form.nome.trim()) errs.nome = "Inserisci il tuo nome";
        if (!form.telefono.trim()) errs.telefono = "Inserisci un recapito telefonico";
        if (!form.messaggio.trim()) errs.messaggio = "Raccontaci in poche parole di cosa hai bisogno";
        setErrors(errs);
        if (Object.keys(errs).length) {
            toast.error("Controlla i campi evidenziati e riprova.");
            return;
        }
        const servizioNome =
            SERVICES.find((s) => s.id === form.servizio)?.name || "Prima visita";
        const msg = `Buongiorno, sono ${form.nome}. Vorrei richiedere un appuntamento per: ${servizioNome}. ${form.messaggio} (Tel: ${form.telefono})`;
        toast.success("Richiesta pronta: ti apriamo WhatsApp per inviarla allo studio.");
        window.open(whatsappLink(msg), "_blank", "noopener,noreferrer");
    };

    const infoItems = [
        {
            icon: MapPin,
            label: "Indirizzo",
            value: CONTACTS.address,
            href: CONTACTS.mapsUrl,
            testid: "contact-address",
        },
        {
            icon: Phone,
            label: "Telefono",
            value: CONTACTS.phoneDisplay,
            href: CONTACTS.phoneHref,
            testid: "contact-phone",
        },
    ];

    return (
        <>
            <section
                data-testid="contatti-hero"
                className="bg-white pt-32 pb-14 sm:pt-40 md:pb-16"
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-8 md:px-12">
                    <FadeUp y={14}>
                        <p className="flex items-center gap-3 text-xs font-medium uppercase tracking-[0.3em] text-petrol">
                            <span className="h-px w-10 bg-petrol" />
                            Contatti & Prenotazioni
                        </p>
                    </FadeUp>
                    <h1 className="mt-7 max-w-3xl font-serif text-4xl font-normal leading-[1.08] tracking-tight text-ink sm:text-5xl md:text-6xl">
                        <MaskedLine delay={0.15}>Parliamone</MaskedLine>
                        <MaskedLine delay={0.3}>
                            <span className="italic text-petrol">di persona.</span>
                        </MaskedLine>
                    </h1>
                </div>
            </section>

            <section
                data-testid="contatti-main"
                className="bg-ice py-16 sm:py-20 md:py-24"
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-8 md:px-12">
                    <FadeUp>
                        <a
                            href={CONTACTS.phoneHref}
                            data-testid="contatti-urgenze-banner"
                            className="group mb-10 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-petrol/25 bg-white p-6 transition-all duration-300 hover:border-petrol/50 hover:shadow-lg"
                        >
                            <div className="flex items-center gap-4">
                                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-petrol text-white">
                                    <Phone className="h-5 w-5" />
                                </span>
                                <div>
                                    <p className="font-serif text-xl font-normal text-ink">
                                        Urgenza in corso?
                                    </p>
                                    <p className="text-sm font-light text-body">
                                        Chiamaci subito: per le urgenze facciamo il
                                        possibile per darti priorità in giornata.
                                    </p>
                                </div>
                            </div>
                            <span className="text-sm font-normal text-petrol transition-colors group-hover:text-petrol-dark">
                                {CONTACTS.phoneDisplay} →
                            </span>
                        </a>
                    </FadeUp>
                </div>
                <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 sm:px-8 md:px-12 lg:grid-cols-12">
                    <div className="space-y-6 lg:col-span-5">
                        {infoItems.map((it, i) => (
                            <FadeUp key={it.label} delay={0.08 * i}>
                                <a
                                    href={it.href}
                                    target={it.href.startsWith("http") ? "_blank" : undefined}
                                    rel="noopener noreferrer"
                                    data-testid={it.testid}
                                    className="group flex items-start gap-4 rounded-2xl border border-line bg-white p-6 transition-all duration-300 hover:border-petrol/40 hover:shadow-lg"
                                >
                                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-petrol-light text-petrol transition-colors duration-300 group-hover:bg-petrol group-hover:text-white">
                                        <it.icon className="h-5 w-5" />
                                    </span>
                                    <span>
                                        <span className="block text-xs font-medium uppercase tracking-[0.22em] text-muted">
                                            {it.label}
                                        </span>
                                        <span className="mt-1 block text-base font-light text-ink">
                                            {it.value}
                                        </span>
                                    </span>
                                </a>
                            </FadeUp>
                        ))}

                        <FadeUp delay={0.3}>
                            <div
                                data-testid="contact-hours-card"
                                className="flex items-start gap-4 rounded-2xl border border-line bg-white p-6"
                            >
                                <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-petrol-light text-petrol">
                                    <Clock className="h-5 w-5" />
                                </span>
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted">
                                        Orari di apertura
                                    </p>
                                    {CONTACTS.hours.map((h) => (
                                        <p
                                            key={h.days}
                                            className="mt-1 text-base font-light text-ink"
                                        >
                                            <span className="text-body">{h.days}:</span>{" "}
                                            {h.time}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        </FadeUp>

                        <FadeUp delay={0.35}>
                            <div
                                data-testid="contact-map-embed"
                                className="overflow-hidden rounded-2xl border border-line"
                            >
                                <iframe
                                    title="Mappa Studio Dentistico Dr. Mario Massironi"
                                    src={CONTACTS.mapsEmbedUrl}
                                    className="aspect-[16/10] w-full"
                                    style={{ border: 0 }}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    allowFullScreen
                                />
                                <a
                                    href={CONTACTS.mapsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-map-link"
                                    className="flex items-center justify-center gap-2 bg-white px-4 py-3.5 text-xs font-medium uppercase tracking-[0.2em] text-petrol transition-colors duration-300 hover:bg-petrol-light"
                                >
                                    <Navigation className="h-4 w-4" />
                                    Apri indicazioni su Google Maps
                                </a>
                            </div>
                        </FadeUp>
                    </div>

                    <FadeUp delay={0.15} className="lg:col-span-7">
                        <form
                            onSubmit={submit}
                            data-testid="contact-form"
                            noValidate
                            className="rounded-3xl border border-line bg-white p-7 shadow-sm sm:p-10"
                        >
                            <h2 className="font-serif text-2xl font-normal text-ink sm:text-3xl">
                                Richiedi un appuntamento
                            </h2>
                            <p className="mt-2 text-sm font-light leading-relaxed text-body">
                                Compila il modulo: al termine ti apriremo WhatsApp
                                con il messaggio già pronto da inviare allo studio.
                                Ti risponderemo appena possibile.
                            </p>

                            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                                <div>
                                    <label
                                        htmlFor="contact-nome"
                                        className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-muted"
                                    >
                                        Nome e cognome *
                                    </label>
                                    <input
                                        id="contact-nome"
                                        data-testid="contact-name-input"
                                        type="text"
                                        value={form.nome}
                                        onChange={set("nome")}
                                        placeholder="Mario Rossi"
                                        className={`${inputCls} ${errors.nome ? "border-red-400" : ""}`}
                                    />
                                    {errors.nome && (
                                        <p data-testid="contact-name-error" className="mt-1.5 text-xs text-red-500">
                                            {errors.nome}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label
                                        htmlFor="contact-telefono"
                                        className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-muted"
                                    >
                                        Telefono *
                                    </label>
                                    <input
                                        id="contact-telefono"
                                        data-testid="contact-phone-input"
                                        type="tel"
                                        value={form.telefono}
                                        onChange={set("telefono")}
                                        placeholder="333 000 0000"
                                        className={`${inputCls} ${errors.telefono ? "border-red-400" : ""}`}
                                    />
                                    {errors.telefono && (
                                        <p data-testid="contact-phone-error" className="mt-1.5 text-xs text-red-500">
                                            {errors.telefono}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label
                                        htmlFor="contact-servizio"
                                        className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-muted"
                                    >
                                        Servizio di interesse
                                    </label>
                                    <select
                                        id="contact-servizio"
                                        data-testid="contact-service-select"
                                        value={form.servizio}
                                        onChange={set("servizio")}
                                        className={inputCls}
                                    >
                                        <option value="">Prima visita / Non so ancora</option>
                                        {SERVICES.map((s) => (
                                            <option key={s.id} value={s.id}>
                                                {s.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label
                                        htmlFor="contact-messaggio"
                                        className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-muted"
                                    >
                                        Come possiamo aiutarti? *
                                    </label>
                                    <textarea
                                        id="contact-messaggio"
                                        data-testid="contact-message-input"
                                        rows={4}
                                        value={form.messaggio}
                                        onChange={set("messaggio")}
                                        placeholder="Raccontaci in poche parole di cosa hai bisogno…"
                                        className={`${inputCls} resize-none ${errors.messaggio ? "border-red-400" : ""}`}
                                    />
                                    {errors.messaggio && (
                                        <p data-testid="contact-message-error" className="mt-1.5 text-xs text-red-500">
                                            {errors.messaggio}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-8 flex flex-wrap items-center gap-4">
                                <button
                                    type="submit"
                                    data-testid="contact-submit-button"
                                    className="inline-flex items-center gap-3 rounded-full bg-petrol px-8 py-4 text-sm font-normal text-white shadow-sm transition-all duration-300 hover:bg-petrol-dark hover:shadow-md active:scale-[0.98]"
                                >
                                    <Send className="h-4 w-4" />
                                    Invia richiesta
                                </button>
                                <a
                                    href={whatsappLink()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-whatsapp-direct"
                                    className="inline-flex items-center gap-2.5 rounded-full border border-wa/60 px-6 py-3.5 text-sm font-normal text-wa-dark transition-all duration-300 hover:bg-wa hover:text-white active:scale-[0.98]"
                                >
                                    <MessageCircle className="h-4 w-4" />
                                    Preferisco scrivere direttamente
                                </a>
                            </div>
                            <p className="mt-5 text-xs font-light leading-relaxed text-muted">
                                Inviando la richiesta accetti di essere ricontattato
                                dallo studio per la gestione dell'appuntamento.
                            </p>
                        </form>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
